import { useCallback, useEffect, useRef } from "react";
import { useUpdateGkvBill } from "../../../../../../stores/Transactions/insurance-mutation";
import { GkvBillEditDto } from "../types/interface";

const DEBOUNCE_MS = 3000;

export interface UseAutoSaveNoteReturn {
  /** Call when user types a note. Debounces save automatically. */
  updateNote: (billId: string, value: string) => void;
  /** Flush all pending saves immediately (call on unmount or before navigation). */
  flushAll: () => void;
}

/**
 * Per-bill note auto-save with 3-second debounce.
 *
 * - Tracks pending changes per billId.
 * - Saves via `useUpdateGkvBill` mutation (only sends notes + existing rxIds).
 * - Flushes all pending on unmount.
 * - No complex ref chains — single Map for timeouts.
 */
export function useAutoSaveNote(bills: GkvBillEditDto[]): UseAutoSaveNoteReturn {
  const updateGkvBill = useUpdateGkvBill();

  // Pending timeouts keyed by billId
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Latest note values keyed by billId (for flush)
  const pendingRef = useRef<Map<string, string>>(new Map());
  // Latest bills ref (avoid stale closure)
  const billsRef = useRef(bills);
  billsRef.current = bills;

  const save = useCallback(
    (billId: string, noteValue: string) => {
      const bill = billsRef.current.find((b) => b.id === billId);
      if (!bill) return;

      updateGkvBill.mutate({
        billId,
        dto: {
          notes: noteValue,
          rxIds: bill.rxs.map((rx) => rx.id),
          amount: bill.amount,
        },
      });

      pendingRef.current.delete(billId);
    },
    [updateGkvBill],
  );

  const updateNote = useCallback(
    (billId: string, value: string) => {
      // Clear existing debounce for this bill
      const existing = timeoutsRef.current.get(billId);
      if (existing) clearTimeout(existing);

      // Track as pending
      pendingRef.current.set(billId, value);

      // Schedule save
      const timeout = setTimeout(() => {
        save(billId, value);
        timeoutsRef.current.delete(billId);
      }, DEBOUNCE_MS);

      timeoutsRef.current.set(billId, timeout);
    },
    [save],
  );

  const flushAll = useCallback(() => {
    // Clear all timeouts and save immediately
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();

    pendingRef.current.forEach((value, billId) => {
      save(billId, value);
    });
  }, [save]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      // Save all pending
      pendingRef.current.forEach((value, billId) => {
        const bill = billsRef.current.find((b) => b.id === billId);
        if (!bill) return;
        // Fire-and-forget on unmount
        updateGkvBill.mutate({
          billId,
          dto: {
            notes: value,
            rxIds: bill.rxs.map((rx) => rx.id),
            amount: bill.amount,
          },
        });
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { updateNote, flushAll };
}
